// HISTORICAL onboarding records (Phase 4): texts and labels used to display the old workflow's data (read-only). The wizard that created and
// advanced these records was retired; onboarding now lives in the dashboard (Store-based) and payments in Billing.

export const WORKFLOW_STATUSES = [
  "draft",
  "invoice_issued",
  "submitted",
  "completed",
  "rejected",
];
export const QUOTATION_STATUSES = [
  "pending",
  "payment_uploaded",
  "approved",
  "rejected",
];

const en = {
  title: "Store Onboarding",
  subtitle:
    "Complete your store details, then review the quotation and payment.",
  stage1: "Store Information",
  stage1Description: "Store, contact, entity, and business documents",
  stage2: "Quotation & Payment",
  stage2Description: "Review your quotation and submit your payment receipt",
  storeInformation: "Store Information",
  storeHelp: "Tell us about the store you want to onboard.",
  contactDetails: "Contact Details",
  contactHelp: "Who should we contact about this onboarding?",
  entityInformation: "Entity Information",
  entityHelp:
    "Enter the legal entity details exactly as they appear in official records.",
  documents: "Business Documents",
  documentsHelp:
    "A commercial registration document is required. VAT is optional.",
  businessName: "Business name",
  city: "City",
  branchCount: "Number of branches",
  ownerName: "Owner name",
  ownerPhone: "Owner phone",
  ownerEmail: "Owner email",
  contactName: "Contact name",
  jobTitle: "Job title",
  mobile: "Mobile number",
  email: "Email",
  entityName: "Legal entity name",
  commercialRegistration: "Commercial registration number",
  crExpiration: "Commercial registration expiry",
  vatNumber: "VAT number",
  notes: "Notes",
  commercialRegistrationDocument: "Commercial registration",
  vatDocument: "VAT certificate",
  upload: "Upload",
  open: "Open",
  remove: "Remove",
  saveDraft: "Save draft",
  submitStage1: "Submit Store Information",
  saving: "Saving…",
  loading: "Loading",
  retry: "Try again",
  noData: "No onboarding record was found.",
  saved: "Your draft has been saved.",
  invoiceIssued: "Invoice Issued",
  draft: "Draft",
  submitted: "Payment Submitted",
  completed: "Completed",
  rejected: "Rejected",
  draftDescription: "Complete the required information before continuing.",
  invoice_issuedDescription:
    "Your quotation is ready. Upload your payment receipt when payment is complete.",
  submittedDescription:
    "Your payment receipt was submitted and is waiting for review.",
  completedDescription: "Your onboarding is complete. Your store is ready.",
  rejectedDescription:
    "This stage needs changes before onboarding can continue.",
  pending: "Pending Payment",
  payment_uploaded: "Waiting for Review",
  approved: "Approved",
  quotationRejected: "Payment Rejected",
  pendingDescription: "Payment has not been submitted yet.",
  payment_uploadedDescription: "The payment receipt is waiting for review.",
  approvedDescription: "Payment was approved.",
  quotationRejectedDescription: "Update and resubmit the payment receipt.",
  rejectedStage: "Rejected stage",
  rejectionReason: "Reason",
  stageRejected: "Stage {stage} rejected",
  quotation: "Quotation",
  quotationSummary: "Quotation Summary",
  basePrice: "Base price",
  addOns: "Add-ons",
  aiPhotos: "AI food photos",
  aiPhotosCount: "Photo count",
  discount: "Discount",
  discountCode: "Discount code",
  apply: "Apply",
  totalBeforeDiscount: "Total before discount",
  discountAmount: "Discount amount",
  finalPrice: "Final price",
  paymentReceipt: "Payment Receipt",
  paymentStatus: "Payment status",
  paymentInstructions:
    "Transfer the final amount to the account below, then upload your receipt.",
  bankName: "Bank",
  accountName: "Account name",
  accountNumber: "Account number",
  iban: "IBAN",
  uploadReceipt: "Upload Payment Receipt",
  receiptHelp: "PDF, JPG, or PNG up to 10 MB.",
  waitingTitle: "Waiting for Review",
  completedTitle: "Onboarding Completed",
  required: "This field is required.",
  invalidEmail: "Enter a valid email address.",
  invalidPhone: "Enter a valid phone number.",
  positiveNumber: "Enter a valid positive number.",
  invalidFile: "Upload a PDF, JPG, or PNG file no larger than 10 MB.",
  missingCr: "Upload the commercial registration document.",
  genericError: "We could not complete that request. Please try again.",
  sar: "SAR",
  currentStage: "Current stage",
  workflowStatus: "Workflow status",
  quotationStatus: "Quotation status",
  search: "Search",
  allStatuses: "All statuses",
  view: "View",
  close: "Close",
  edit: "Edit information",
  issueInvoice: "Issue Invoice",
  updateQuotation: "Update Quotation",
  reviewPayment: "Review Payment",
  approveOnboarding: "Approve Onboarding",
  rejectStage: "Reject Stage",
  confirm: "Confirm",
  cancel: "Cancel",
  rejectionPrompt: "Select the stage and explain what needs to be corrected.",
  reasonRequired: "Enter a rejection reason.",
  stageRequired: "Select a stage.",
  noRequests: "No onboarding requests found.",
  created: "Created",
  details: "Onboarding Details",
  paymentNotUploaded: "No payment receipt uploaded.",
  noDocuments: "No documents uploaded.",
  file: "File",
  receipt: "Payment receipt",
  quotationValidation:
    "Prices cannot be negative and discount must be between 0 and 100%.",
  actions: "Actions",
  openFile: "Open File",
};

const ar = {
  title: "تهيئة المتجر",
  subtitle: "أكملي بيانات المتجر، ثم راجعي عرض السعر والدفع.",
  stage1: "معلومات المتجر",
  stage1Description: "بيانات المتجر والتواصل والكيان والمستندات",
  stage2: "عرض السعر والدفع",
  stage2Description: "راجعي عرض السعر وأرسلي إيصال الدفع",
  storeInformation: "معلومات المتجر",
  storeHelp: "أدخلي بيانات المتجر المراد تهيئته.",
  contactDetails: "بيانات التواصل",
  contactHelp: "بيانات الشخص المسؤول عن متابعة طلب التهيئة.",
  entityInformation: "بيانات الكيان",
  entityHelp: "أدخلي بيانات الكيان كما تظهر في السجلات الرسمية.",
  documents: "مستندات المنشأة",
  documentsHelp: "السجل التجاري مطلوب، وشهادة الضريبة اختيارية.",
  businessName: "اسم النشاط",
  city: "المدينة",
  branchCount: "عدد الفروع",
  ownerName: "اسم المالك",
  ownerPhone: "جوال المالك",
  ownerEmail: "بريد المالك",
  contactName: "اسم جهة التواصل",
  jobTitle: "المسمى الوظيفي",
  mobile: "رقم الجوال",
  email: "البريد الإلكتروني",
  entityName: "اسم الكيان القانوني",
  commercialRegistration: "رقم السجل التجاري",
  crExpiration: "تاريخ انتهاء السجل",
  vatNumber: "الرقم الضريبي",
  notes: "ملاحظات",
  commercialRegistrationDocument: "السجل التجاري",
  vatDocument: "شهادة الضريبة",
  upload: "رفع",
  open: "فتح",
  remove: "حذف",
  saveDraft: "حفظ المسودة",
  submitStage1: "إرسال معلومات المتجر",
  saving: "جارٍ الحفظ…",
  loading: "جارٍ التحميل",
  retry: "إعادة المحاولة",
  noData: "لم يتم العثور على طلب تهيئة.",
  saved: "تم حفظ المسودة.",
  invoiceIssued: "تم إصدار الفاتورة",
  draft: "مسودة",
  submitted: "تم إرسال الدفع",
  completed: "مكتمل",
  rejected: "مرفوض",
  draftDescription: "أكملي البيانات المطلوبة للمتابعة.",
  invoice_issuedDescription:
    "عرض السعر جاهز. ارفعي إيصال الدفع بعد إتمام التحويل.",
  submittedDescription: "تم إرسال إيصال الدفع وهو بانتظار المراجعة.",
  completedDescription: "اكتملت تهيئة متجرك بنجاح.",
  rejectedDescription: "تحتاج هذه المرحلة إلى تعديل قبل المتابعة.",
  pending: "بانتظار الدفع",
  payment_uploaded: "بانتظار المراجعة",
  approved: "معتمد",
  quotationRejected: "الدفع مرفوض",
  pendingDescription: "لم يتم إرسال الدفع بعد.",
  payment_uploadedDescription: "إيصال الدفع بانتظار المراجعة.",
  approvedDescription: "تم اعتماد الدفع.",
  quotationRejectedDescription: "حدّثي إيصال الدفع وأعيدي إرساله.",
  rejectedStage: "المرحلة المرفوضة",
  rejectionReason: "السبب",
  stageRejected: "المرحلة {stage} مرفوضة",
  quotation: "عرض السعر",
  quotationSummary: "ملخص عرض السعر",
  basePrice: "السعر الأساسي",
  addOns: "الإضافات",
  aiPhotos: "صور الأطباق بالذكاء الاصطناعي",
  aiPhotosCount: "عدد الصور",
  discount: "الخصم",
  discountCode: "رمز الخصم",
  apply: "تطبيق",
  totalBeforeDiscount: "الإجمالي قبل الخصم",
  discountAmount: "قيمة الخصم",
  finalPrice: "السعر النهائي",
  paymentReceipt: "إيصال الدفع",
  paymentStatus: "حالة الدفع",
  paymentInstructions:
    "حوّلي المبلغ النهائي إلى الحساب أدناه، ثم ارفعي إيصال الدفع.",
  bankName: "البنك",
  accountName: "اسم الحساب",
  accountNumber: "رقم الحساب",
  iban: "الآيبان",
  uploadReceipt: "رفع إيصال الدفع",
  receiptHelp: "PDF أو JPG أو PNG، بحد أقصى 10 ميجابايت.",
  waitingTitle: "بانتظار المراجعة",
  completedTitle: "اكتملت التهيئة",
  required: "هذا الحقل مطلوب.",
  invalidEmail: "أدخلي بريداً إلكترونياً صحيحاً.",
  invalidPhone: "أدخلي رقم جوال صحيحاً.",
  positiveNumber: "أدخلي رقماً موجباً صحيحاً.",
  invalidFile: "ارفعي ملف PDF أو JPG أو PNG لا يتجاوز 10 ميجابايت.",
  missingCr: "ارفعي مستند السجل التجاري.",
  genericError: "تعذر إكمال الطلب. حاولي مرة أخرى.",
  sar: "ر.س",
  currentStage: "المرحلة الحالية",
  workflowStatus: "حالة الطلب",
  quotationStatus: "حالة عرض السعر",
  search: "بحث",
  allStatuses: "كل الحالات",
  view: "عرض",
  close: "إغلاق",
  edit: "تعديل المعلومات",
  issueInvoice: "إصدار الفاتورة",
  updateQuotation: "تحديث عرض السعر",
  reviewPayment: "مراجعة الدفع",
  approveOnboarding: "اعتماد التهيئة",
  rejectStage: "رفض مرحلة",
  confirm: "تأكيد",
  cancel: "إلغاء",
  rejectionPrompt: "اختاري المرحلة واكتبي التعديلات المطلوبة.",
  reasonRequired: "اكتبي سبب الرفض.",
  stageRequired: "اختاري المرحلة.",
  noRequests: "لا توجد طلبات تهيئة.",
  created: "تاريخ الإنشاء",
  details: "تفاصيل التهيئة",
  paymentNotUploaded: "لم يتم رفع إيصال دفع.",
  noDocuments: "لم يتم رفع مستندات.",
  file: "ملف",
  receipt: "إيصال الدفع",
  quotationValidation:
    "يجب ألا تكون الأسعار سالبة، وأن تكون نسبة الخصم بين 0 و100٪.",
  actions: "الإجراءات",
  openFile: "فتح الملف",
};

export const ONBOARDING_TEXT = { en, ar };
export const getText = (lang) => ONBOARDING_TEXT[lang] || en;
export const getStageMeta = (stage, lang) => {
  const t = getText(lang);
  const value = Number(stage) === 2 ? 2 : 1;
  return {
    value,
    label: t[`stage${value}`],
    description: t[`stage${value}Description`],
  };
};
export const getWorkflowStatusMeta = (status, lang) => {
  const t = getText(lang);
  const value = WORKFLOW_STATUSES.includes(status) ? status : "draft";
  return {
    label: value === "invoice_issued" ? t.invoiceIssued : t[value],
    description: t[`${value}Description`],
    variant: {
      draft: "neutral",
      invoice_issued: "primary",
      submitted: "warning",
      completed: "success",
      rejected: "danger",
    }[value],
  };
};
export const getQuotationStatusMeta = (status, lang) => {
  const t = getText(lang);
  const value = QUOTATION_STATUSES.includes(status) ? status : "pending";
  const key = value === "rejected" ? "quotationRejected" : value;
  return {
    label: t[key],
    description: t[`${key}Description`],
    variant: {
      pending: "neutral",
      payment_uploaded: "warning",
      approved: "success",
      rejected: "danger",
    }[value],
  };
};
export const formatDate = (value, lang) => {
  if (!value) return "-";
  return new Date(value).toLocaleString(lang === "ar" ? "ar-SA" : "en-US");
};